import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard, FolderKanban, ScanLine, BookMarked, HardHat,
  Sun, Moon, LogOut, PanelLeftClose, PanelLeft, Ruler, Menu, X, Home, UserCog, Library,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: Home, id: "home" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/projects", label: "Projects", icon: FolderKanban, id: "projects" },
  { to: "/catalog", label: "Cost Catalog", icon: BookMarked, id: "catalog" },
  { to: "/pricebook", label: "Price Book", icon: Library, id: "pricebook" },
];

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isDark = theme !== "light";
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`${collapsed ? "w-20" : "w-64"} shrink-0 border-r border-border bg-popover transition-all duration-300 hidden md:flex flex-col sticky top-0 h-screen`}
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <div className="h-9 w-9 rounded-lg bg-primary grid place-items-center shrink-0">
            <HardHat className="h-5 w-5 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-extrabold tracking-tight">SmartBuild</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">AI Estimator</div>
            </div>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const active = isActive(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.id}
                to={n.to}
                data-testid={`nav-${n.id}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                  active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{n.label}</span>}
              </Link>
            );
          })}
        </nav>
        <button
          data-testid="sidebar-collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          className="m-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          {collapsed ? <PanelLeft className="h-5 w-5" /> : <><PanelLeftClose className="h-5 w-5" /><span>Collapse</span></>}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 max-w-[80%] bg-popover border-r border-border flex flex-col animate-fade-up">
            <div className="h-16 flex items-center justify-between px-5 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-primary grid place-items-center"><HardHat className="h-5 w-5 text-primary-foreground" /></div>
                <div className="leading-tight">
                  <div className="font-extrabold tracking-tight">SmartBuild</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">AI Estimator</div>
                </div>
              </div>
              <button data-testid="mobile-nav-close" onClick={() => setMobileOpen(false)} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1">
              {NAV.map((n) => {
                const active = isActive(n.to);
                const Icon = n.icon;
                return (
                  <Link key={n.id} to={n.to} data-testid={`mobile-nav-${n.id}`} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition-colors ${
                      active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}>
                    <Icon className="h-5 w-5 shrink-0" /><span>{n.label}</span>
                  </Link>
                );
              })}
              <Link to="/profile" data-testid="mobile-nav-profile" onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition-colors ${
                  isActive("/profile") ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
                <UserCog className="h-5 w-5 shrink-0" /><span>Profile & Settings</span>
              </Link>
            </nav>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
            <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}
              className="md:hidden h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-secondary transition-colors -ml-1 mr-1" aria-label="Open menu">
              <Menu className="h-5 w-5 text-foreground" />
            </button>
            <Ruler className="hidden sm:block h-4 w-4 text-primary" />
            <span className="uppercase tracking-widest text-xs">{NAV.find((n) => isActive(n.to))?.label || "Project"}</span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button
              data-testid="theme-toggle-btn"
              onClick={() => setTheme(isDark ? "light" : "dark")}
              className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-secondary transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/profile" data-testid="profile-link" className="flex items-center gap-2 pl-1 rounded-lg hover:bg-secondary/60 transition-colors py-1 pr-2">
              <div className="h-9 w-9 rounded-full bg-primary/20 text-primary grid place-items-center text-sm font-bold overflow-hidden">
                {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U").toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-semibold max-w-[140px] truncate">{user?.name}</div>
                <div className="text-[11px] text-muted-foreground max-w-[140px] truncate">{user?.email}</div>
              </div>
            </Link>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-destructive/15 hover:text-destructive hover:border-destructive/40 transition-colors"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-24 md:pb-6">{children}</main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 bg-popover/95 backdrop-blur-xl border-t border-border flex items-stretch"
      >
        {NAV.map((n) => {
          const active = location.pathname.startsWith(n.to);
          const Icon = n.icon;
          return (
            <Link
              key={n.id}
              to={n.to}
              data-testid={`bottom-nav-${n.id}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${
                active ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-5 w-5" />
              <span className="truncate max-w-full px-0.5">{n.label}</span>
            </Link>
          );
        })}
        <button
          data-testid="bottom-nav-more"
          onClick={() => setMobileOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <Menu className="h-5 w-5" />
          <span>More</span>
        </button>
      </nav>
    </div>
  );
}
